import { useRef } from "react";

import { ORG_GEO_FIELDS, ORG_GEO_SIMS } from "constants/settings.constants";

import useUserModules from "hooks/useUserModules";

import { GeoEntitlement, isGeoJobAllowed } from "../utils/evaResearchEntitlement";
import { useGeoSticksCalculationJobs } from "./useGeoSticksCalculationJobs";
import { useGetGeoCalculationsJobs } from "./useGetGeoCalculationsJobs";

export type VisibleJobNames = Record<string, Set<string>>;

interface JobLike {
  name?: string;
  modelSource?: string;
  plays?: string[];
}

interface JobListQuery {
  data?: JobLike[];
  isSuccess: boolean;
}

/**
 * Empty until the list has answered, so nothing job-owned shows before it loads.
 * A name shared with an allowed job is allowed.
 */
function visibleNames(query: JobListQuery, entitlement: GeoEntitlement): Set<string> {
  const visible = new Set<string>();
  if (!query.isSuccess) return visible;
  for (const job of query.data ?? []) {
    if (job.name && isGeoJobAllowed(job, entitlement)) visible.add(job.name);
  }
  return visible;
}

/**
 * Which org-geo job names the Data Table and field search may offer, for surfaces
 * that render for every role. Reads only the two job lists: the access list is the
 * whole org's private models with no server role check, so it is never touched here.
 *
 * The server omits unentitled EVA Research jobs; the entitlement check stays so the
 * UI is right whichever side deploys first. Same keys as the other readers of these
 * lists, so no extra requests.
 */
export function useVisibleOrgGeoJobNames(enabled: boolean): VisibleJobNames {
  const { hasGeology, has3dGeoModel, geoPlays } = useUserModules();
  const calcQuery = useGetGeoCalculationsJobs({ enabled });
  const simQuery = useGeoSticksCalculationJobs(undefined, { enabled, poll: false });

  const entitlement = { hasGeology, has3dGeoModel, geoPlays };
  const next: VisibleJobNames = {
    [ORG_GEO_FIELDS]: visibleNames(calcQuery, entitlement),
    [ORG_GEO_SIMS]: visibleNames(simQuery, entitlement)
  };

  // Consumers key an effect on this value, so equal content must be the same object.
  // The loaded flag keeps loading and a loaded empty list apart.
  const resultRef = useRef<VisibleJobNames>(next);
  const keyRef = useRef<Maybe<string>>(null);
  const loaded = [calcQuery.isSuccess, simQuery.isSuccess];
  const key = JSON.stringify([
    loaded,
    Object.entries(next).map(([group, names]) => [group, [...names].sort()])
  ]);
  if (key !== keyRef.current) {
    keyRef.current = key;
    resultRef.current = next;
  }
  return resultRef.current;
}
